/**
 * Message Store
 * Zustand store for managing message state
 */

import create from 'zustand';
import type { Message, Conversation } from '../api/types';
import logger from '../utils/logger';

interface ConversationState {
  conversation: Conversation | null;
  messages: Message[];
  isLoading: boolean;
  isSending: boolean;
  error: string | null;
  rateLimitRemaining: number;
  rateLimitResetAt: string | null;

  // Actions
  setConversation: (conversation: Conversation) => void;
  addMessage: (message: Message) => void;
  addMessages: (messages: Message[]) => void;
  setLoading: (loading: boolean) => void;
  setSending: (sending: boolean) => void;
  setError: (error: string | null) => void;
  setRateLimit: (remaining: number, resetAt: string) => void;
  clearMessage: () => void;
  clearError: () => void;
}

export const useConversationStore = create<ConversationState>((set) => ({
  conversation: null,
  messages: [],
  isLoading: false,
  isSending: false,
  error: null,
  rateLimitRemaining: 50,
  rateLimitResetAt: null,

  setConversation: (conversation) => {
    // Ensure messages is always an array
    const messages = Array.isArray(conversation.messages) ? conversation.messages : [];
    logger.log('[messageStore.setConversation] Conversation messages type:', typeof conversation.messages, 'isArray:', Array.isArray(conversation.messages));
    logger.log('[messageStore.setConversation] Raw messages:', conversation.messages);

    set({
      conversation,
      messages: messages.filter(msg => msg && msg.id), // Filter out invalid messages
      error: null,
    });
  },

  addMessage: (message) =>
    set((state) => {
      if (!message || !message.id) return state;
      // Deduplicate: skip if message already exists (prevents WebSocket + REST duplicates)
      if (state.messages.some((m) => m.id === message.id)) return state;
      return { messages: [...state.messages, message] };
    }),

  addMessages: (messages) =>
    set((state) => {
      const validMessages = messages.filter(msg => msg && msg.id);
      // Deduplicate against existing messages
      const existingIds = new Set(state.messages.map((m) => m.id));
      const newMessages = validMessages.filter((m) => !existingIds.has(m.id));
      if (newMessages.length === 0) return state;
      return { messages: [...state.messages, ...newMessages] };
    }),

  setLoading: (isLoading) => set({ isLoading }),

  setSending: (isSending) => set({ isSending }),

  setError: (error) => set({ error }),

  setRateLimit: (rateLimitRemaining, rateLimitResetAt) =>
    set({ rateLimitRemaining, rateLimitResetAt }),

  clearMessage: () =>
    set({
      conversation: null,
      messages: [],
      error: null,
    }),

  clearError: () => set({ error: null }),
}));

